import { getOnlyDate, isBefore } from '~/astra'
import { useMultiQuery } from '~/hooks/tanstack-query/entities/useMultiQuery'
import {
    importLocationScheduleActivityLogs,
    importPractitionerScheduleActivityLogs,
    importPractitionerScheduleLocationActivityLogs,
    importPractitionerScheduleStatusActivityLogs,
} from '~/store/di-entity.api'
import {
    useLocationScheduleActivityLogs,
    usePractitionerScheduleActivityLogs,
    usePractitionerScheduleLocationActivityLogs,
    usePractitionerScheduleStatusActivityLogs,
} from '~/storm'

import { DateLocationCell, DatePractitionerCell, isDatePractitionerCell } from '../utils'

function sortActivityLogsByPerformedAtDesc<T extends { performed_at: string }>(logs: readonly T[]): T[] {
    return logs.toSorted((a, b) => (isBefore(a.performed_at, b.performed_at) ? 1 : -1))
}

export const useActivityLogs = (selectedCell: DatePractitionerCell | DateLocationCell) => {
    const date = getOnlyDate(selectedCell.date)
    const isPractitionerCell = isDatePractitionerCell(selectedCell)
    const practitionerId = isPractitionerCell ? selectedCell.practitionerId : -1
    const locationId = isPractitionerCell ? -1 : selectedCell.locationId

    const scheduleActivityLogs = usePractitionerScheduleActivityLogs({
        where: { scheduled_date: date, practitioner_id: practitionerId },
    })

    const statusActivityLogs = usePractitionerScheduleStatusActivityLogs({
        where: { scheduled_date: date, practitioner_id: practitionerId },
        include: { statusDefinition: true },
    })

    const practitionerLocationActivityLogs = usePractitionerScheduleLocationActivityLogs({
        where: isPractitionerCell ? { scheduled_date: date, practitioner_id: practitionerId } : { scheduled_date: date, location_id: locationId },
        include: { location: true },
    })

    const locationActivityLogs = useLocationScheduleActivityLogs({
        where: { scheduled_date: date, location_id: locationId },
    })

    if (isPractitionerCell) {
        const scheduleActivityLogsWithSyntheticTypes = scheduleActivityLogs.map((activityLog, i) => ({
            ...activityLog,
            // Workaround until we get a proper comment system.
            // This is necessary because comment is attached to PractitionerSchedule, so CRUD operations are not set correctly.
            activity_type: i === 0 ? ('create' as const) : activityLog.comment === '' ? ('delete' as const) : ('update' as const),
        }))

        return sortActivityLogsByPerformedAtDesc([...scheduleActivityLogsWithSyntheticTypes, ...statusActivityLogs, ...practitionerLocationActivityLogs])
    }

    return sortActivityLogsByPerformedAtDesc([...locationActivityLogs, ...practitionerLocationActivityLogs])
}

function getFetchQueries(selectedCell?: DatePractitionerCell | DateLocationCell) {
    if (!selectedCell) return []

    const queryParams = isDatePractitionerCell(selectedCell)
        ? ({ practitioner_id: selectedCell.practitionerId, scheduled_date: getOnlyDate(selectedCell.date) } as const)
        : ({ location_id: selectedCell.locationId, scheduled_date: getOnlyDate(selectedCell.date) } as const)
    const queryKey = isDatePractitionerCell(selectedCell) ? { practitioner_id: selectedCell.practitionerId } : { location_id: selectedCell.locationId }

    if (isDatePractitionerCell(selectedCell)) {
        return [
            {
                queryFn: () => importPractitionerScheduleActivityLogs(queryParams),
                queryKey: ['practitionerScheduleActivityLogs', queryKey],
            },
            {
                queryFn: () => importPractitionerScheduleStatusActivityLogs(queryParams),
                queryKey: ['practitionerScheduleStatusActivityLogs', queryKey],
            },
            {
                queryFn: () => importPractitionerScheduleLocationActivityLogs(queryParams),
                queryKey: ['practitionerScheduleLocationActivityLogs', queryKey],
            },
        ]
    }

    return [
        {
            queryFn: () => importLocationScheduleActivityLogs(queryParams),
            queryKey: ['locationScheduleActivityLogs', queryKey],
        },
        {
            queryFn: () => importPractitionerScheduleLocationActivityLogs(queryParams),
            queryKey: ['practitionerScheduleLocationActivityLogs', queryKey],
        },
    ]
}

export const useImportActivityLogs = (selectedCell?: DatePractitionerCell | DateLocationCell) => {
    const imports = getFetchQueries(selectedCell)
    return useMultiQuery(imports)
}
